/**
 * What the hotbar says about the tool you have equipped.
 *
 * The player crafts up the ladder (Wood → Stone → Copper → Iron → Gold → Mythril)
 * and the hotbar looked identical afterwards: same sprite, same slot, no number.
 * The owner's complaint was exactly that — "I have mythril, I don't see the
 * change". So the tier is painted onto the slot itself: the metal's colour washes
 * the icon (same tintIcon path the bag panel already uses) plus a short label,
 * because on a phone there is no hover to reveal a tooltip.
 *
 * The colours are NOT a new palette — they are tierTint from map/ores.ts, the same
 * table the vein art, the held-item tint and the bag slots read. Wood and Stone
 * have no metal there (tierTint returns null) and keep the icon's own art.
 *
 * Kept as pure functions in their own module so the label/colour contract can be
 * pinned by a test — hud.ts itself is Pixi-and-DOM and cannot be asserted in jsdom.
 */
import { tierTint } from '../../map/ores';

/**
 * Same order and words as backend crafting/gear.ts TOOL_TIER_NAME (which is now
 * crafting.config's TIER_NAME). Tiers 7-9 were missing here, so a crafted Ruby
 * pickaxe wore a "My / Mythril" badge in the hotbar while the crafting screen
 * called it Ruby — the exact mismatch the pinning test below exists to catch.
 */
export const TOOL_TIER_NAME = ['—', 'Wood', 'Stone', 'Copper', 'Iron', 'Gold', 'Mythril',
  'Emerald', 'Amber', 'Ruby'] as const;

/**
 * Two-letter badge under the icon. A 48px slot cannot fit "Mythril" at the UI
 * font (and the font is not up for changing), so it gets the chemical-symbol
 * shorthand the metals already carry — Cu/Fe/Au read as metals at a glance and the
 * colour behind them disambiguates the rest.
 */
// Em/Am/Ru are not chemical symbols (the top three are gemstones, not metals),
// but they are the same two-letter shape and the tier colour behind them is the
// real disambiguator.
export const TOOL_TIER_SHORT = ['—', 'Wd', 'St', 'Cu', 'Fe', 'Au', 'My',
  'Em', 'Am', 'Ru'] as const;

const TOP_TIER = TOOL_TIER_NAME.length - 1;
const clamp = (tier: number) => (Number.isFinite(tier) ? Math.max(0, Math.min(TOP_TIER, Math.floor(tier))) : 0);

export const toolTierName = (tier: number): string => TOOL_TIER_NAME[clamp(tier)];
export const toolTierShort = (tier: number): string => TOOL_TIER_SHORT[clamp(tier)];

/** The metal's colour, or null for Wood/Stone/none — "leave the sprite alone". */
export const toolTierColor = (tier: number): string | null => tierTint(clamp(tier));

/** Which hotbar slots carry a crafted tier at all (torch, bag and the item previews do not). */
export type TieredTool = 'knife' | 'hoe' | 'axe' | 'pickaxe';

/** hotbar id → the key it has in FarmCtx.gearTiers (the sword slot is called "knife" on the bar). */
export const TOOL_TIER_KEY: Record<TieredTool, 'sword' | 'hoe' | 'axe' | 'pickaxe'> = {
  knife: 'sword', hoe: 'hoe', axe: 'axe', pickaxe: 'pickaxe',
};

/** Live numbers the farm already has from the status endpoints; all optional. */
export interface TierFacts {
  chopEnergy?: number;
  mineEnergy?: number;
  orePerRock?: number;
  gemChance?: number;
}

/**
 * What a tier-0 slot honestly says.
 *
 * The knife slot reads the SWORD tier, which is 0 on a fresh farm — and the first
 * draft printed "not crafted yet" on a slot that works perfectly: combat/damage.ts
 * swingDamage = FIST_DAMAGE + weaponDamage(0), i.e. bare fists still hit for 3. The
 * other three tools really are unusable until crafted (there is no tier-0 axe to
 * swing), so only the knife gets the different sentence.
 */
function untieredNote(tool: TieredTool): string {
  return tool === 'knife'
    ? 'Bare fists — craft a sword for more damage per hit'
    : 'Not crafted yet';
}

/**
 * One honest sentence about what THIS tier buys.
 *
 * Deliberately not "bigger yields" for the axe: felling pays by TREE SIZE now
 * (woodcutting.service WOOD_BY_SIZE), so a mythril axe drops exactly as much wood
 * as a wooden one and only costs less energy per swing. Saying otherwise would
 * teach the player a rule the server does not follow. The pickaxe genuinely does
 * still raise ore and gem chance (gear.ts PICKAXE), so it says so; the hoe has no
 * tier effect anywhere in the backend, and pretending it does would be worse than
 * admitting it.
 */
export function toolTierEffect(tool: TieredTool, tier: number, facts: TierFacts = {}): string {
  if (clamp(tier) <= 0) return untieredNote(tool);
  switch (tool) {
    case 'axe':
      return facts.chopEnergy != null
        ? `${facts.chopEnergy} energy per swing — logs depend on tree size, not the axe`
        : 'Cheaper swings — logs depend on tree size, not the axe';
    case 'pickaxe': {
      const bits = [
        facts.mineEnergy != null ? `${facts.mineEnergy} energy per swing` : 'cheaper swings',
        facts.orePerRock != null ? `${facts.orePerRock} ore per rock` : null,
        facts.gemChance != null ? `${Math.round(facts.gemChance * 100)}% gem chance` : null,
      ].filter(Boolean);
      return bits.join(' · ');
    }
    case 'knife':
      return `+${clamp(tier) * 3} damage per hit`;
    case 'hoe':
      return 'Tills the same at every tier';
  }
}

/** The slot's `title=`: names the tier in words, then what it actually does. */
export function toolSlotTitle(label: string, tool: TieredTool, tier: number, facts: TierFacts = {}): string {
  return clamp(tier) <= 0
    ? `${label} — ${untieredNote(tool).toLowerCase()}`
    : `${label} · ${toolTierName(tier)} — ${toolTierEffect(tool, tier, facts)}`;
}
